import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type {
  ChangeEvent,
  CompositionEvent,
  FormEvent,
  KeyboardEvent,
  SyntheticEvent,
} from "react";
import type { SMSTemplateProvenance } from "@/components/domain/communication/sms-templates";

/** Textarea selection is retained with each draft for exact undo restoration. @agentic connections */
type Selection = {
  start: number;
  end: number;
  direction: "forward" | "backward" | "none";
};

/** Draft content and attribution always travel together through history. @agentic connections */
type Snapshot = {
  text: string;
  selection: Selection;
  provenance?: SMSTemplateProvenance;
};

/** Only adjacent edits of the same kind can join an existing typing transaction. @agentic connections */
type TypingGroup = {
  inputType: string;
  timestamp: number;
  selection: Selection;
};

/** Limits sensitive draft history to the most recent 100 transactions in memory. @agentic connections */
const HISTORY_LIMIT = 100;

/** Maximum gap between adjacent keystrokes in a single undo transaction. @agentic connections */
const TYPING_INTERVAL_MS = 500;

/** Creates an empty draft without sharing mutable selection objects. @agentic connections */
const emptySnapshot = (): Snapshot => ({
  text: "",
  selection: { start: 0, end: 0, direction: "none" },
});

/** Reads UTF-16 offsets, matching the browser's textarea selection API. @agentic connections */
const readSelection = (element: HTMLTextAreaElement): Selection => ({
  start: element.selectionStart,
  end: element.selectionEnd,
  direction: element.selectionDirection,
});

/** Compares caret locations without conflating forward and backward selections. @agentic connections */
const sameSelection = (left: Selection, right: Selection) =>
  left.start === right.start &&
  left.end === right.end &&
  left.direction === right.direction;

/** Infers adjacent text edits when a browser does not supply beforeinput/inputType. @agentic connections */
const inferInputType = (before: Snapshot, after: Snapshot): string => {
  if (before.selection.start !== before.selection.end) return "replacement";
  const caret = before.selection.start;
  const delta = after.text.length - before.text.length;
  if (
    delta > 0 &&
    after.text.slice(0, caret) === before.text.slice(0, caret) &&
    after.text.slice(caret + delta) === before.text.slice(caret)
  )
    return "insertText";
  if (
    delta < 0 &&
    after.text ===
      before.text.slice(0, caret + delta) + before.text.slice(caret)
  )
    return "deleteContentBackward";
  if (
    delta < 0 &&
    after.text ===
      before.text.slice(0, caret) + before.text.slice(caret - delta)
  )
    return "deleteContentForward";
  return "replacement";
};

/**
 * Owns an in-memory SMS draft and its transactional undo/redo history.
 * Mount inside the drawer's patient/user keyed body to discard history on identity changes.
 * @agentic connections
 */
export const useSMSComposer = () => {
  const [snapshot, setSnapshot] = useState<Snapshot>(emptySnapshot);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const current = useRef<Snapshot>(emptySnapshot());
  const past = useRef<Snapshot[]>([]);
  const future = useRef<Snapshot[]>([]);
  const typing = useRef<TypingGroup | null>(null);
  const pendingInputType = useRef<string | null>(null);
  const composition = useRef<Snapshot | null>(null);
  const completedComposition = useRef<{
    before: Snapshot;
    recorded: boolean;
  } | null>(null);
  const restoreSelection = useRef(false);

  const publish = useCallback((next: Snapshot, restore = false) => {
    current.current = next;
    restoreSelection.current = restore;
    setSnapshot(next);
  }, []);

  const captureSelection = useCallback(() => {
    const element = textareaRef.current;
    // A noncancelable native undo may already have changed the DOM. Its stale
    // value/selection must never overwrite our authoritative draft snapshot.
    if (element && element.value === current.current.text) {
      current.current = {
        ...current.current,
        selection: readSelection(element),
      };
    }
    return current.current;
  }, []);

  const remember = useCallback((previous: Snapshot) => {
    past.current.push(previous);
    if (past.current.length > HISTORY_LIMIT) past.current.shift();
    future.current = [];
  }, []);

  const moveHistory = useCallback(
    (direction: "undo" | "redo", capture = true) => {
      if (composition.current) return;
      completedComposition.current = null;
      const present = capture ? captureSelection() : current.current;
      const source = direction === "undo" ? past.current : future.current;
      const destination = direction === "undo" ? future.current : past.current;
      const restored = source.pop();
      if (restored) destination.push(present);
      typing.current = null;
      pendingInputType.current = null;
      // Publish even at the boundary: an uncancelable native history input may
      // need its DOM mutation replaced, including after a successful-send reset.
      publish(restored ?? { ...present }, true);
    },
    [captureSelection, publish],
  );

  useLayoutEffect(() => {
    const element = textareaRef.current;
    if (!restoreSelection.current || !element) return;
    restoreSelection.current = false;
    element.focus({ preventScroll: true });
    element.setSelectionRange(
      snapshot.selection.start,
      snapshot.selection.end,
      snapshot.selection.direction,
    );
  }, [snapshot]);

  useEffect(() => {
    const element = textareaRef.current;
    if (!element) return;
    // React's beforeinput normalization does not expose every native history
    // event. Listen directly so Edit-menu undo shares the keyboard history.
    const beforeInput = (event: InputEvent) => {
      if (
        event.inputType === "historyUndo" ||
        event.inputType === "historyRedo"
      ) {
        if (event.cancelable && !composition.current) {
          event.preventDefault();
          moveHistory(event.inputType === "historyUndo" ? "undo" : "redo");
        }
        return;
      }
      captureSelection();
      pendingInputType.current = event.inputType || null;
    };
    element.addEventListener("beforeinput", beforeInput);
    return () => element.removeEventListener("beforeinput", beforeInput);
  }, [captureSelection, moveHistory]);

  const onChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      const native = event.nativeEvent as InputEvent;
      const inputType = native.inputType || pendingInputType.current;
      pendingInputType.current = null;
      if (inputType === "historyUndo" || inputType === "historyRedo") {
        moveHistory(inputType === "historyUndo" ? "undo" : "redo", false);
        return;
      }
      const before = current.current;
      const text = event.currentTarget.value;
      const next: Snapshot = {
        text,
        selection: readSelection(event.currentTarget),
        provenance: text
          ? (composition.current ?? before).provenance
          : undefined,
      };
      if (composition.current) {
        publish(next);
        return;
      }
      const completed = completedComposition.current;
      completedComposition.current = null;
      // Some browsers commit the final IME text after compositionend. It is
      // still the same transaction as the intermediate composition updates.
      if (
        completed &&
        (inputType === "insertFromComposition" ||
          inputType === "insertCompositionText")
      ) {
        if (!completed.recorded && text !== completed.before.text)
          remember(completed.before);
        publish({
          ...next,
          provenance: text ? completed.before.provenance : undefined,
        });
        return;
      }
      if (text === before.text) return;

      const kind = inputType || inferInputType(before, next);
      const timestamp = Date.now();
      const canGroup =
        Boolean(text) &&
        [
          "insertText",
          "deleteContentBackward",
          "deleteContentForward",
        ].includes(kind) &&
        before.selection.start === before.selection.end &&
        next.selection.start === next.selection.end;
      const previousGroup = typing.current;
      const joinsGroup =
        canGroup &&
        previousGroup &&
        previousGroup.inputType === kind &&
        timestamp - previousGroup.timestamp <= TYPING_INTERVAL_MS &&
        sameSelection(previousGroup.selection, before.selection);
      if (!joinsGroup) remember(before);
      else future.current = [];
      typing.current = canGroup
        ? { inputType: kind, timestamp, selection: next.selection }
        : null;
      publish(next);
    },
    [moveHistory, publish, remember],
  );

  const onSelect = useCallback((event: SyntheticEvent<HTMLTextAreaElement>) => {
    const selection = readSelection(event.currentTarget);
    if (!sameSelection(selection, current.current.selection))
      typing.current = null;
    current.current = { ...current.current, selection };
  }, []);

  const onBeforeInput = useCallback(
    (event: FormEvent<HTMLTextAreaElement>) => {
      if (!event.isDefaultPrevented()) captureSelection();
    },
    [captureSelection],
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (
        composition.current ||
        event.nativeEvent.isComposing ||
        event.nativeEvent.keyCode === 229 ||
        event.altKey ||
        !(event.metaKey || event.ctrlKey)
      )
        return;
      const key = event.key.toLowerCase();
      const isUndo = key === "z" && !event.shiftKey;
      const isRedo =
        (key === "z" && event.shiftKey) || (key === "y" && event.ctrlKey);
      if (!isUndo && !isRedo) return;
      event.preventDefault();
      moveHistory(isUndo ? "undo" : "redo");
    },
    [moveHistory],
  );

  const prepareClipboardEdit = useCallback(
    (inputType: string) => {
      captureSelection();
      typing.current = null;
      pendingInputType.current = inputType;
    },
    [captureSelection],
  );

  const onCompositionStart = useCallback(() => {
    composition.current = captureSelection();
    completedComposition.current = null;
    typing.current = null;
    pendingInputType.current = null;
  }, [captureSelection]);

  const onCompositionEnd = useCallback(
    (event: CompositionEvent<HTMLTextAreaElement>) => {
      const before = composition.current;
      if (!before) return;
      composition.current = null;
      pendingInputType.current = null;
      const text = event.currentTarget.value;
      const recorded = text !== before.text;
      completedComposition.current = { before, recorded };
      if (recorded) remember(before);
      publish({
        text,
        selection: readSelection(event.currentTarget),
        provenance: text ? before.provenance : undefined,
      });
    },
    [publish, remember],
  );

  const replaceTemplate = useCallback(
    (text: string, provenance: SMSTemplateProvenance) => {
      remember(captureSelection());
      typing.current = null;
      composition.current = null;
      completedComposition.current = null;
      pendingInputType.current = null;
      publish(
        {
          text,
          provenance: { ...provenance },
          selection: {
            start: text.length,
            end: text.length,
            direction: "none",
          },
        },
        true,
      );
    },
    [captureSelection, publish, remember],
  );

  const reset = useCallback(() => {
    past.current = [];
    future.current = [];
    typing.current = null;
    pendingInputType.current = null;
    composition.current = null;
    completedComposition.current = null;
    publish(emptySnapshot());
  }, [publish]);

  return {
    text: snapshot.text,
    provenance: snapshot.provenance,
    textareaRef,
    textareaProps: {
      value: snapshot.text,
      onChange,
      onBeforeInput,
      onSelect,
      onKeyDown,
      onCompositionStart,
      onCompositionEnd,
      onPaste: () => prepareClipboardEdit("insertFromPaste"),
      onCut: () => prepareClipboardEdit("deleteByCut"),
      onBlur: () => {
        typing.current = null;
      },
    },
    replaceTemplate,
    reset,
    isComposing: () => composition.current !== null,
  };
};
